import { Group } from "@fairfleet/geotab";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useVehicleGroupIds } from "../../hooks/useVehicleGroupIds";
import { compareGroupNames } from "../../utils/groupHierarchy";
import { useGroupOptionRenderer } from "../common/GroupOption";
import { useGroups } from "@/hooks/groups/useGroups";

interface Props {
  value: Group[];
  onChange: (groups: Group[]) => void;
  inputRef?: Ref<HTMLInputElement>;
}

/** Page-level group filter, applied on top of Geotab's global group filter. */
export function GroupMultiSelect({ value, onChange, inputRef }: Props) {
  const { t } = useTranslation();
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const { vehicleGroupIds, isLoading: vehiclesLoading } = useVehicleGroupIds();
  const isLoading = groupsLoading || vehiclesLoading;
  const options = useMemo(
    () => groups.filter(g => !!g.name && vehicleGroupIds.has(g.id)).sort(compareGroupNames),
    [groups, vehicleGroupIds],
  );
  const renderOption = useGroupOptionRenderer(groups);

  return (
    <Autocomplete
      id="logbook-audit-groups"
      multiple
      size="small"
      fullWidth
      limitTags={1}
      disableCloseOnSelect
      loading={isLoading}
      options={options}
      value={value}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={option => option.name}
      renderOption={renderOption}
      onChange={(_, next) => onChange(next)}
      renderInput={params => (
        <TextField {...params} inputRef={inputRef} label={t("logbookAudit.filters.groups")} placeholder={t("logbookAudit.filters.allGroups")} />
      )}
    />
  );
}
